import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { isAfter } from 'date-fns';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import menuPortalProps from 'helpers/reactSelectPortal';
import AsyncSelect from 'react-select/async';
import CreatableSelect from 'react-select/creatable';
import { FaInfoCircle, FaCar, FaRegCalendarAlt, FaMapMarkerAlt, FaUsers, FaBell, FaBuilding } from 'react-icons/fa';
import { MdCancel, MdCarCrash, MdUpdate } from 'react-icons/md';
import { createBooking, updateBooking } from 'redux/booking/car/carBookingSlice';
import { getProfileUser } from 'redux/profileUserSlice';
import { getActiveSites } from 'redux/organization/siteSlice';
import { employeesEmailDebounce } from 'helpers/employeeOptionHelper';
import { Button, FormRow, Accordion } from 'components/common';
import { FormHero } from 'components/app/bookings/common/FormLayout';

// Accordion section header — blue icon chip + title (mirrors the travel request form).
const accHeader = (Icon, text) => (
  <span className="tw-flex tw-items-center tw-gap-2.5">
    <span className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded tw-bg-blue-100 tw-text-blue-600 tw-flex-shrink-0"><Icon size={13} /></span>
    <span className="tw-font-semibold tw-text-slate-700 tw-text-sm">{text}</span>
  </span>
);

import { INPUT_CLS as inputClass } from 'helpers/formStyles';
const hintClass = 'tw-mt-1.5 tw-text-xs tw-text-slate-500 tw-flex tw-items-center tw-gap-1';

const TransactionForm = ({ title, transaction, travel, embedded = false, onClose, onSuccess }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue, getValues, reset, setError, clearErrors } = useForm();
  const [luggageCheck, setLuggageCheck] = useState(transaction && transaction.luggage !== null);
  const [passengers, setPassengers] = useState([]);
  const [activeKeys, setActiveKeys] = useState(['0', '1', '2', '3']);
  const { data: profileUser } = useSelector((state) => state.profile_user);
  const { activeData: siteData = [] } = useSelector((state) => state.organization_site);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const emails = useMemo(
    () => (travel ? travel.employees.map((item) => item.employee_email) : null),
    [travel]
  );

  const defaultEmailOption = useMemo(() => {
    if (emails) {
      return emails.map((option) => ({ value: option, label: option }));
    }
    if (transaction && transaction.cc_email) {
      return transaction.cc_email.map((option) => ({ value: option, label: option }));
    }
    return null;
  }, [transaction, emails]);


  useEffect(() => {
    if (!transaction) reset();
  }, [title, transaction, reset]);

  useEffect(() => {
    if (profileUser === undefined) dispatch(getProfileUser());
    if (siteData?.length === 0) dispatch(getActiveSites());
    // mount-only lookup fetch; depending on siteData.length would refetch in a loop whenever the site list comes back empty
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setValue('from_date', new Date());
    setValue('site_id', profileUser.site?.id);

    if (travel) {
      setValue('from_date', new Date(travel.date_of_travel));
      setValue('to_date', new Date(travel.date_of_return));
      setValue('description', travel.description_purpose_of_travel);
      setValue('pickup_location', travel.site_name);
      setValue('site_id', travel.site_id);

      const phones = travel.employees
        .filter((item) => item.employee_phone !== '' && item.employee_phone !== null)
        .map((item) => item.employee_phone);
      if (phones) setValue('phone_contacts', phones.join(', '));
      if (emails) setValue('cc_email', emails.join(';'));

      setPassengers(travel.employees.map((emp) => ({ value: emp.employee_name, label: emp.employee_name })));
    }

    if (transaction) {
      Object.entries(transaction).forEach(([key, value]) => {
        if (key === 'from_date' || key === 'to_date') {
          setValue(key, new Date(value));
        } else if (key === 'name_of_passengers') {
          setPassengers(value.map((item) => ({ value: item, label: item })));
        } else {
          setValue(key, value);
        }
      });
    }
    // seed only when transaction/travel change; re-running when profileUser/emails load later would overwrite the user's edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, transaction, travel]);

  useEffect(() => {
    setValue('name_of_passengers', passengers.map((p) => p.label).join(', '));
    setValue('number_of_passengers', passengers.length);
    if (passengers.length > 0) clearErrors('name_of_passengers');
  }, [passengers, setValue, clearErrors]);

  const onSubmit = async (formData) => {
    if (passengers.length === 0) {
      setError('name_of_passengers', { type: 'manual', message: 'Name of passengers must be required' });
      return;
    }

    let data = formData;
    if (travel) data = { ...data, travel_booking_id: travel.id };

    try {
      let transactionId = '';
      if (transaction) {
        transactionId = transaction.id;
        dispatch(updateBooking({ transactionId, data }));
      } else {
        const response = await dispatch(createBooking(data));
        transactionId = response.payload.data.id;
      }
      if (embedded) onSuccess?.(transactionId);
      else navigate(`/booking/car/${transactionId}`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const siteOptions = siteData.filter((item) => item.car_booking === true).map((item) => ({ value: item.id, label: item.name }));
  const emailOptions = (inputValue, callback) => employeesEmailDebounce(inputValue, callback);
  const handleBack = () => (embedded ? onClose?.() : navigate(-1));

  const handleChangePassengers = (selectedOptions) => {
    // Normalize newly-typed names: trim, drop blanks, de-duplicate by label.
    const cleaned = (selectedOptions || [])
      .map((option) => ({ value: option.value.trim(), label: option.label.trim() }))
      .filter((option) => option.label !== '');
    const unique = cleaned.filter((option, index) => cleaned.findIndex((o) => o.label === option.label) === index);
    setPassengers(unique);
  };

  const handleChangeCCemail = (selectedOptions) => {
    setValue('cc_email', selectedOptions.map((option) => option.value).join(';'), { shouldValidate: true });
  };

  const validateBookingDate = (value) => {
    const fromDate = getValues('from_date');
    const parsedFrom = fromDate ? new Date(fromDate) : null;
    const parsedTo = value ? new Date(value) : null;
    return (parsedFrom && parsedTo && isAfter(parsedTo, parsedFrom)) || 'To date must be after From date.';
  };

  const formContent = (
    <form onSubmit={handleSubmit(onSubmit)} className={embedded ? '' : 'tw-p-4'}>
      <Accordion activeKeys={activeKeys} onChange={setActiveKeys}>
            <Accordion.Item eventKey="0" header={accHeader(FaRegCalendarAlt, 'Schedule & route')}>
              <div className="tw-p-4">
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-4">
                <FormRow label="From date" required error={errors.from_date?.message}>
                  <Controller
                    control={control}
                    name="from_date"
                    rules={{ required: 'From date must be required.' }}
                    render={({ field }) => (
                      <DatePicker
                        selected={field.value}
                        onChange={(date) => field.onChange(date)}
                        className={inputClass}
                        placeholderText="DD-MM-YYYY h:mm aa"
                        timeIntervals={5}
                        dateFormat="dd-MM-yyyy h:mm aa"
                        showTimeSelect
                        fixedHeight
                        portalId="rdp-portal"
                        popperClassName="tw-z-[100100]"
                      />
                    )}
                  />
                </FormRow>

                <FormRow label="To date" required error={errors.to_date?.message}>
                  <Controller
                    control={control}
                    name="to_date"
                    rules={{ required: 'To date must be required.', validate: { afterFromDate: (v) => validateBookingDate(v) } }}
                    render={({ field }) => (
                      <DatePicker
                        selected={field.value}
                        onChange={(date) => field.onChange(date)}
                        className={inputClass}
                        placeholderText="DD-MM-YYYY h:mm aa"
                        timeIntervals={5}
                        dateFormat="dd-MM-yyyy h:mm aa"
                        showTimeSelect
                        fixedHeight
                        portalId="rdp-portal"
                        popperClassName="tw-z-[100100]"
                      />
                    )}
                  />
                </FormRow>
              </div>

              <FormRow label="Route" required>
                <Controller
                  control={control}
                  name="route"
                  defaultValue={transaction?.route || 'One way'}
                  render={({ field }) => (
                    <div className="tw-flex tw-gap-3 tw-mt-1">
                      {['One way', 'Round trip'].map((option) => {
                        const selected = field.value === option;
                        return (
                          <label key={option}
                            className={`tw-inline-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-px-3 tw-py-2 tw-rounded tw-border tw-border-solid tw-text-sm tw-transition-colors ${selected ? 'tw-border-blue-500 tw-bg-blue-50 tw-text-blue-700 tw-font-medium' : 'tw-border-slate-200 tw-text-slate-600 hover:tw-border-blue-300'}`}>
                            <input type="radio" value={option} checked={selected} onChange={() => field.onChange(option)} />
                            {option}
                          </label>
                        );
                      })}
                    </div>
                  )}
                />
              </FormRow>
              </div>
            </Accordion.Item>

            <Accordion.Item eventKey="1" header={accHeader(FaMapMarkerAlt, 'Locations & purpose')}>
              <div className="tw-p-4">
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-4">
                <FormRow label="Address pickup location" required error={errors.pickup_location?.message}>
                  <textarea rows={3} placeholder="Address pickup location" className={inputClass}
                    {...register('pickup_location', { required: 'Address pickup location must be required.' })} />
                </FormRow>

                <FormRow label="Address drop location" required error={errors.drop_location?.message}>
                  <textarea rows={3} placeholder="Address drop location" className={inputClass}
                    {...register('drop_location', { required: 'Address drop location must be required.' })} />
                </FormRow>
              </div>

              <FormRow label="Purpose trip" required error={errors.description?.message}>
                <textarea rows={5} placeholder="Purpose trip" className={inputClass}
                  {...register('description', { required: 'Purpose trip must be required.' })} />
              </FormRow>
              </div>
            </Accordion.Item>

            <Accordion.Item eventKey="2" header={accHeader(FaUsers, 'Passengers & contact')}>
              <div className="tw-p-4">
              <FormRow label="Name of passengers" required error={errors.name_of_passengers?.message}>
                <CreatableSelect
                  isMulti
                  isClearable
                  value={passengers}
                  onChange={handleChangePassengers}
                  placeholder="Type a name and press Enter..."
                  classNamePrefix="react-select" {...menuPortalProps}
                  formatCreateLabel={(input) => `Add "${input}"`}
                  noOptionsMessage={() => 'Type a name and press Enter to add'}
                />
                <div className={hintClass}><FaInfoCircle /> Type each passenger name and press Enter to add. Add multiple names as needed.</div>
              </FormRow>

              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-4">
                <FormRow label="Number of passengers" required error={errors.number_of_passengers?.message}>
                  <input type="number" min={0} max={20} placeholder="Number of passengers" className={`${inputClass} disabled:tw-bg-slate-50`} disabled
                    {...register('number_of_passengers', { required: 'Number of passengers must be required' })} />
                  <div className={hintClass}><FaInfoCircle /> Auto-filled from the passenger names above.</div>
                </FormRow>

                <FormRow label="Phone contacts" required error={errors.phone_contacts?.message}>
                  <input type="text" placeholder="Phone contacts" className={inputClass}
                    {...register('phone_contacts', { required: 'Phone contacts must be required' })} />
                </FormRow>
              </div>

              <FormRow label="Luggage">
                <div className="tw-flex tw-items-start tw-gap-4">
                  <label className="tw-inline-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-mt-1">
                    <input type="checkbox" checked={luggageCheck} onChange={(e) => setLuggageCheck(e.target.checked)} />
                    <span className="tw-text-sm tw-text-slate-600">{luggageCheck ? 'Yes' : 'None'}</span>
                  </label>
                  {luggageCheck && (
                    <div className="tw-flex-1">
                      <textarea rows={3} placeholder="Luggage" className={inputClass}
                        {...register('luggage', { required: 'Luggage must be required' })} />
                      {errors.luggage && <p className="tw-text-xs tw-text-red-600 tw-mt-1 tw-mb-0">{errors.luggage.message}</p>}
                    </div>
                  )}
                </div>
              </FormRow>
              </div>
            </Accordion.Item>

            <Accordion.Item eventKey="3" header={accHeader(FaBell, 'Notification & office')}>
              <div className="tw-p-4">
              <FormRow label="Site" required error={errors.site_id?.message}>
                <Controller
                  control={control}
                  name="site_id"
                  defaultValue={transaction?.site_id || profileUser.site?.id}
                  rules={{ required: { value: true, message: 'Site must be required' } }}
                  render={({ field }) => (
                    <Select
                      closeMenuOnSelect
                      options={siteOptions}
                      placeholder="Select..."
                      classNamePrefix="react-select" {...menuPortalProps}
                      value={siteOptions.find((option) => option.value === field.value) || null}
                      formatOptionLabel={(o) => <span className="tw-flex tw-items-center tw-gap-2"><FaBuilding className="tw-text-slate-400" size={12} />{o.label}</span>}
                      onChange={(selectedOption) => field.onChange(selectedOption.value)}
                    />
                  )}
                />
                <div className={hintClass}><FaInfoCircle /> Specify the correct site office, as the booking request will be forwarded to the HR Admin of that office for arrangement.</div>
              </FormRow>

              <FormRow label="CC emails (optional)">
                <Controller
                  control={control}
                  name="cc_email"
                  render={() => (
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      defaultValue={defaultEmailOption}
                      loadOptions={emailOptions}
                      closeMenuOnSelect={false}
                      placeholder="Select..."
                      classNamePrefix="react-select" {...menuPortalProps}
                      isMulti
                      onChange={handleChangeCCemail}
                    />
                  )}
                />
              </FormRow>
              </div>
            </Accordion.Item>

            </Accordion>

            <div className={`tw-flex tw-justify-center tw-gap-3 tw-border-t tw-border-solid tw-border-slate-200 ${embedded ? 'tw-mt-6 tw-pt-4' : '-tw-mx-4 -tw-mb-4 tw-mt-6 tw-px-4 tw-py-3.5 tw-bg-slate-50 tw-rounded-b-lg'}`}>
              <Button size="sm" type="submit" variant="primary" leftIcon={transaction ? MdUpdate : MdCarCrash}>
                {transaction ? 'Update' : 'Request Booking'}
              </Button>
              <Button size="sm" variant="secondary" leftIcon={MdCancel} onClick={handleBack}>Cancel</Button>
            </div>
    </form>
  );

  if (embedded) return formContent;

  return (
    <div className="tw-p-4">
      <div className="tw-max-w-4xl tw-mx-auto">
        <FormHero
          icon={FaCar}
          title={title}
          subtitle="After you request, the booking is sent directly to Admin for arrangement · Vietnam only (If oversea booking, please contact Admin directly for settlement)."
          onBack={handleBack}
        />
        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-solid tw-border-slate-200 tw-mt-3">
          {formContent}
        </div>
      </div>
    </div>
  );
};

TransactionForm.propTypes = {
  title: PropTypes.string,
  transaction: PropTypes.object,
  travel: PropTypes.object,
  embedded: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
};

export default TransactionForm;
